import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, concatMap, exhaustMap, map, of, switchMap, takeUntil, takeWhile, timer } from "rxjs";
import { AttioClient, AttioQuoteMigrationState as RunState } from "src/api-service";
import { normalizeError } from "../../../../app-store-error.types";
import { AttioQuoteMigrationActions } from "./attio-quote-migration.actions";

/** How often a running migration's status is fetched. */
const POLL_INTERVAL_MS = 3000;

@Injectable()
export class AttioQuoteMigrationEffects {
    private readonly actions$ = inject(Actions);
    private readonly attioClient = inject(AttioClient);

    loadPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioQuoteMigrationActions.loadPreview),
            switchMap(() =>
                this.attioClient.previewQuoteMigration().pipe(
                    map(preview => AttioQuoteMigrationActions.loadPreviewSuccess({ preview })),
                    catchError(error =>
                        of(AttioQuoteMigrationActions.loadPreviewFailure({
                            error: normalizeError(error),
                            toastKey: "DEVELOPER.ATTIO_QUOTE_MIGRATION.PREVIEW_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    start$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioQuoteMigrationActions.start),
            concatMap(() =>
                this.attioClient.startQuoteMigration().pipe(
                    map(status => AttioQuoteMigrationActions.startSuccess({
                        status,
                        toastKey: "DEVELOPER.ATTIO_QUOTE_MIGRATION.START_SUCCESS",
                    })),
                    catchError(error =>
                        of(AttioQuoteMigrationActions.startFailure({
                            error: normalizeError(error),
                            toastKey: "DEVELOPER.ATTIO_QUOTE_MIGRATION.START_ERROR",
                        })),
                    ),
                ),
            ),
        ),
    );

    /** A started run, or one another developer started (409), is followed until it finishes. */
    pollAfterStart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioQuoteMigrationActions.startSuccess, AttioQuoteMigrationActions.startFailure),
            map(() => AttioQuoteMigrationActions.startPolling()),
        ),
    );

    /** Fetches the status now, then every few seconds while the run is running. */
    poll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttioQuoteMigrationActions.startPolling),
            switchMap(() =>
                timer(0, POLL_INTERVAL_MS).pipe(
                    exhaustMap(() => this.attioClient.getQuoteMigrationStatus()),
                    takeWhile(status => status.state === RunState.RUNNING, true),
                    map(status => AttioQuoteMigrationActions.loadStatusSuccess({ status })),
                    catchError(error =>
                        of(AttioQuoteMigrationActions.loadStatusFailure({
                            error: normalizeError(error),
                            toastKey: "DEVELOPER.ATTIO_QUOTE_MIGRATION.STATUS_ERROR",
                        })),
                    ),
                    takeUntil(this.actions$.pipe(ofType(AttioQuoteMigrationActions.stopPolling))),
                ),
            ),
        ),
    );
}
